import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronRight,
  AlertTriangle,
  Shield,
} from 'lucide-react'
import { comparisons, type Comparison } from '@/data/comparisons'
import { useSEO } from '@/hooks/useSEO'
import { buildAuthorSchema, buildPublisherSchema } from '@/lib/authorSchema'
import { MarketingHeader } from '@/components/marketing/MarketingHeader'
import { MarketingFooter } from '@/components/marketing/MarketingFooter'

export function CompareList() {
  useSEO({
    title: 'Compare DocumentIQ',
    description:
      'See how DocumentIQ compares to ABBYY FlexiCapture, AWS Textract, and manual data entry for document extraction.',
    canonical: 'https://documentiq.algoscale.com/compare',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'DocumentIQ Comparisons',
        url: 'https://documentiq.algoscale.com/compare',
        mainEntity: {
          '@type': 'ItemList',
          itemListElement: comparisons.map((c, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            url: `https://documentiq.algoscale.com/compare/${c.slug}`,
            name: c.headline,
          })),
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://documentiq.algoscale.com' },
          { '@type': 'ListItem', position: 2, name: 'Compare', item: 'https://documentiq.algoscale.com/compare' },
        ],
      },
    ],
  })

  return (
    <div className="min-h-screen bg-white text-gray-900 antialiased">
      <MarketingHeader />

      <section className="py-16 sm:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-gray-900 leading-tight">
            Compare DocumentIQ
          </h1>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            Honest, side-by-side comparisons so you can decide if DocumentIQ is the right fit for your document extraction needs.
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {comparisons.map((comp) => (
              <Link
                key={comp.slug}
                to={`/compare/${comp.slug}`}
                className="group flex flex-col rounded-2xl border border-gray-200 bg-white hover:border-blue-200 hover:shadow-lg hover:shadow-blue-50 transition-all duration-300 p-6 sm:p-8"
              >
                <span className="inline-flex self-start px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium mb-3">
                  Comparison
                </span>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors leading-snug">
                  {comp.headline}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-6 flex-1">
                  {comp.description}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <span className="text-xs text-gray-500">{comp.rows.length} features compared</span>
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 group-hover:gap-2 transition-all">
                    View comparison
                    <ChevronRight size={16} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}

export function CompareDetail() {
  const { slug } = useParams<{ slug: string }>()
  const comp = comparisons.find((c) => c.slug === slug)

  const jsonLd = useMemo(() => {
    if (!comp) return undefined
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'DocumentIQ',
        description: comp.description,
        brand: { '@type': 'Brand', name: 'DocumentIQ' },
        category: 'AI Document Extraction Software',
        url: `https://documentiq.algoscale.com/compare/${comp.slug}`,
        // image is required by Google's Product rich-result validator;
        // without it GSC flags this as a critical error. Reuse the
        // per-page OG card so SERP previews and Product carousels
        // surface the same visual.
        image: `https://documentiq.algoscale.com/og/compare/${comp.slug}.png`,
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
          url: 'https://documentiq.algoscale.com/register',
          // shippingDetails + hasMerchantReturnPolicy are optional in
          // schema.org but Google flags their absence as warnings on
          // every Product. For a free digital service: zero shipping
          // cost worldwide, no returns applicable.
          shippingDetails: {
            '@type': 'OfferShippingDetails',
            shippingRate: {
              '@type': 'MonetaryAmount',
              value: '0',
              currency: 'USD',
            },
            shippingDestination: {
              '@type': 'DefinedRegion',
              geoMidpoint: { '@type': 'GeoCoordinates', latitude: 0, longitude: 0 },
            },
            deliveryTime: {
              '@type': 'ShippingDeliveryTime',
              handlingTime: { '@type': 'QuantitativeValue', minValue: 0, maxValue: 0, unitCode: 'DAY' },
              transitTime: { '@type': 'QuantitativeValue', minValue: 0, maxValue: 0, unitCode: 'DAY' },
            },
          },
          hasMerchantReturnPolicy: {
            '@type': 'MerchantReturnPolicy',
            applicableCountry: 'US',
            returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted',
          },
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: comp.headline,
        description: comp.description,
        about: [
          { '@type': 'SoftwareApplication', name: 'DocumentIQ' },
          { '@type': 'Thing', name: comp.competitor },
        ],
        author: buildAuthorSchema('Algoscale'),
        publisher: buildPublisherSchema(),
        mainEntityOfPage: `https://documentiq.algoscale.com/compare/${comp.slug}`,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://documentiq.algoscale.com' },
          { '@type': 'ListItem', position: 2, name: 'Compare', item: 'https://documentiq.algoscale.com/compare' },
          { '@type': 'ListItem', position: 3, name: comp.headline, item: `https://documentiq.algoscale.com/compare/${comp.slug}` },
        ],
      },
    ]
  }, [comp])

  useSEO({
    title: comp ? comp.headline : 'Comparison',
    description: comp?.description || 'DocumentIQ comparison',
    canonical: comp ? `https://documentiq.algoscale.com/compare/${comp.slug}` : undefined,
    ogImage: comp ? `/og/compare/${comp.slug}.png` : undefined,
    jsonLd,
  })

  if (!comp) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Comparison not found.</p>
          <Link to="/compare" className="text-blue-600 font-medium hover:underline">View all comparisons</Link>
        </div>
      </div>
    )
  }

  const otherComparisons = comparisons.filter((c) => c.slug !== comp.slug)

  return (
    <div className="min-h-screen bg-white text-gray-900 antialiased">
      <MarketingHeader />

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/compare" className="hover:text-gray-900 transition-colors flex items-center gap-1">
              <ArrowLeft size={14} /> Compare
            </Link>
            <span>/</span>
            <span className="text-gray-900 font-medium">{comp.competitor}</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="py-12 sm:py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium mb-4">
            Comparison
          </span>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 leading-tight mb-6">
            {comp.headline}
          </h1>
          <p className="text-gray-600 leading-relaxed max-w-3xl">
            {comp.competitorDescription}
          </p>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-12 sm:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Feature-by-feature comparison</h2>
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-5 py-4 font-semibold text-gray-700 w-1/5">Feature</th>
                  <th className="text-left px-5 py-4 font-semibold text-blue-600 w-2/5">
                    <span className="inline-flex items-center gap-1.5">
                      <Check size={16} className="text-green-500" />
                      DocumentIQ
                    </span>
                  </th>
                  <th className="text-left px-5 py-4 font-semibold text-gray-700 w-2/5">{comp.competitor}</th>
                </tr>
              </thead>
              <tbody>
                {comp.rows.map((row, i) => (
                  <tr key={row.feature} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                    <td className="px-5 py-4 font-medium text-gray-900 align-top border-r border-gray-100">
                      {row.feature}
                    </td>
                    <td className="px-5 py-4 text-gray-700 align-top border-r border-gray-100 bg-blue-50/30">
                      {row.documentiq}
                    </td>
                    <td className="px-5 py-4 text-gray-600 align-top">
                      {row.competitor}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Why choose DocumentIQ */}
      <section className="py-12 sm:py-16 bg-gradient-to-b from-white to-blue-50/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2.5 mb-8">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-green-100">
              <Check size={18} className="text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Why choose DocumentIQ</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {comp.advantages.map((adv) => (
              <div
                key={adv}
                className="flex items-start gap-3 rounded-xl border border-green-100 bg-white p-5"
              >
                <div className="mt-0.5 flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full bg-green-100">
                  <Check size={12} className="text-green-600" />
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{adv}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Where competitor might be better */}
      <section className="py-12 sm:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2.5 mb-8">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100">
              <Shield size={18} className="text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Where {comp.competitor} might be better</h2>
          </div>
          <p className="text-sm text-gray-500 mb-6">
            We believe in honest comparisons. Here are scenarios where {comp.competitor} could be a better fit.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {comp.disadvantages.map((dis) => (
              <div
                key={dis}
                className="flex items-start gap-3 rounded-xl border border-amber-100 bg-amber-50/30 p-5"
              >
                <div className="mt-0.5 flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full bg-amber-100">
                  <AlertTriangle size={12} className="text-amber-600" />
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{dis}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-12 sm:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700 p-8 sm:p-12 text-center text-white">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              Ready to try DocumentIQ?
            </h2>
            <p className="text-blue-100 mb-8 max-w-xl mx-auto">
              Start extracting structured data from your documents in minutes. No templates, no complex setup, no credit card required.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white text-blue-700 font-semibold hover:bg-blue-50 transition-colors shadow-sm"
              >
                Start for Free
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/compare"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-blue-400 text-white font-medium hover:bg-blue-500/20 transition-colors"
              >
                View All Comparisons
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Other comparisons */}
      {otherComparisons.length > 0 && (
        <section className="py-12 sm:py-16 bg-gray-50 border-t border-gray-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Other comparisons</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {otherComparisons.map((other) => (
                <Link
                  key={other.slug}
                  to={`/compare/${other.slug}`}
                  className="group flex items-center justify-between rounded-xl border border-gray-200 bg-white hover:border-blue-200 hover:shadow-md transition-all p-5"
                >
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                      {other.headline}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">{other.rows.length} features compared</p>
                  </div>
                  <ChevronRight size={16} className="text-gray-400 group-hover:text-blue-600 transition-colors" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <MarketingFooter />
    </div>
  )
}
